import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  GraduationCap, Plus, Calendar, MapPin, Video, Users, Edit, Trash2, Loader2, X,
  ExternalLink, Copy, Check, Trash, UserPlus,
} from 'lucide-react';
import apiClient from '@/services/api/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { PageHeader } from '@/components/shared/PageHeader';
import { RichTextEditor } from '@/components/shared/RichTextEditor';
import { ImageUpload } from '@/components/shared/ImageUpload';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

type Presenter = {
  name: string;
  title?: string | null;
  imageUrl?: string | null;
  bio?: string | null;
};

type Workshop = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  presenterName: string | null;
  presenterTitle: string | null;
  presenters: Presenter[];
  coverImageUrl: string | null;
  startsAt: string;
  endsAt: string | null;
  locationType: string;
  location: string | null;
  meetingUrl: string | null;
  capacity: number | null;
  status: 'upcoming' | 'cancelled' | 'completed';
  topics: string[];
  agenda: { time: string; title: string }[];
  registrantCount: number;
};

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://ronidoc.data-corner.com.au';

function fmtForInput(iso: string): string {
  const d = new Date(iso);
  // datetime-local needs YYYY-MM-DDTHH:mm
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function WorkshopsAdminPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Workshop | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-workshops'],
    queryFn: async () => {
      const r = await apiClient.get<{ data: { workshops: Workshop[] } }>('/admin/workshops');
      return r.data.data.workshops;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiClient.delete(`/admin/workshops/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-workshops'] });
      toast.success('Workshop deleted');
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Delete failed'),
  });

  const upcoming = (data || []).filter(w => new Date(w.startsAt).getTime() >= Date.now());
  const past = (data || []).filter(w => new Date(w.startsAt).getTime() < Date.now());

  return (
    <div className="space-y-6">
      <PageHeader
        icon={GraduationCap}
        title="Workshops"
        description="Schedule public workshops, manage registrants, share Zoom links."
      />

      <div className="flex justify-end">
        <Button onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4 mr-1.5" /> New workshop
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (data || []).length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <GraduationCap className="h-10 w-10 text-muted-foreground/50" />
            <p className="mt-4 text-sm font-medium">No workshops scheduled yet</p>
            <p className="mt-1 text-xs text-muted-foreground">Create your first workshop to start collecting registrations.</p>
            <Button onClick={() => setCreating(true)} className="mt-5">
              <Plus className="h-4 w-4 mr-1.5" /> New workshop
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {upcoming.length > 0 && (
            <section>
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Upcoming</p>
              <div className="space-y-3">
                {upcoming.map(w => <WorkshopRow key={w.id} ws={w} onEdit={() => setEditing(w)} onDelete={() => {
                  if (confirm(`Delete "${w.title}" and all ${w.registrantCount} registrant(s)? This cannot be undone.`)) deleteMutation.mutate(w.id);
                }} />)}
              </div>
            </section>
          )}
          {past.length > 0 && (
            <section>
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 mt-8">Past</p>
              <div className="space-y-3 opacity-80">
                {past.map(w => <WorkshopRow key={w.id} ws={w} onEdit={() => setEditing(w)} onDelete={() => {
                  if (confirm(`Delete "${w.title}" and all ${w.registrantCount} registrant(s)? This cannot be undone.`)) deleteMutation.mutate(w.id);
                }} />)}
              </div>
            </section>
          )}
        </>
      )}

      {creating && <WorkshopFormDialog open={creating} workshop={null} onClose={() => setCreating(false)} />}
      {editing && <WorkshopFormDialog open={true} workshop={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function WorkshopRow({ ws, onEdit, onDelete }: { ws: Workshop; onEdit: () => void; onDelete: () => void }) {
  const [copiedLink, setCopiedLink] = useState(false);
  const start = new Date(ws.startsAt);
  const isFull = ws.capacity != null && ws.registrantCount >= ws.capacity;
  const publicUrl = `${SITE_URL}/workshops/${ws.slug}`;

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 1500);
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 flex flex-col items-center justify-center w-16 h-16 rounded-xl bg-primary/10 border border-primary/30">
            <span className="text-[10px] uppercase tracking-wider text-primary">{start.toLocaleDateString('en-AU', { month: 'short' })}</span>
            <span className="text-2xl font-bold leading-none">{start.getDate()}</span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2 flex-wrap">
              <p className="text-base font-semibold truncate">{ws.title}</p>
              <Badge variant="outline" className={cn(
                'text-[10px]',
                ws.status === 'cancelled' && 'border-rose-500/40 text-rose-600',
                ws.status === 'completed' && 'border-stone-500/40 text-muted-foreground',
                ws.status === 'upcoming' && 'border-emerald-500/40 text-emerald-600',
              )}>
                {ws.status}
              </Badge>
            </div>
            {ws.subtitle && <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{ws.subtitle}</p>}
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                {start.toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true })}
              </span>
              <span className="inline-flex items-center gap-1.5">
                {ws.locationType === 'online' ? <Video className="h-3.5 w-3.5" /> : <MapPin className="h-3.5 w-3.5" />}
                {ws.locationType === 'online' ? 'Online' : ws.location || 'TBA'}
              </span>
              <span className={cn('inline-flex items-center gap-1.5', isFull && 'text-amber-600 dark:text-amber-400 font-semibold')}>
                <Users className="h-3.5 w-3.5" />
                {ws.registrantCount}{ws.capacity != null ? ` / ${ws.capacity}` : ''}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button asChild size="sm" variant="outline">
              <Link to={`/panel/admin/workshops/${ws.id}/registrants`}>
                <Users className="h-3.5 w-3.5 mr-1.5" /> Registrants
              </Link>
            </Button>
            <Button size="sm" variant="ghost" onClick={onEdit}><Edit className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="ghost" onClick={() => copy(publicUrl)} title="Copy public URL">
              {copiedLink ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
            <Button asChild size="sm" variant="ghost">
              <a href={publicUrl} target="_blank" rel="noreferrer"><ExternalLink className="h-3.5 w-3.5" /></a>
            </Button>
            <Button size="sm" variant="ghost" onClick={onDelete} className="text-rose-600 hover:text-rose-700">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function WorkshopFormDialog({ open, workshop, onClose }: { open: boolean; workshop: Workshop | null; onClose: () => void }) {
  const qc = useQueryClient();
  const isEdit = !!workshop;

  const initialPresenters: Presenter[] = (workshop?.presenters && workshop.presenters.length > 0)
    ? workshop.presenters
    : workshop?.presenterName
      ? [{ name: workshop.presenterName, title: workshop.presenterTitle, imageUrl: null }]
      : [{ name: '', title: '', imageUrl: null }];

  const [form, setForm] = useState({
    title: workshop?.title || '',
    slug: workshop?.slug || '',
    subtitle: workshop?.subtitle || '',
    description: workshop?.description || '',
    coverImageUrl: workshop?.coverImageUrl || null as string | null,
    startsAt: workshop?.startsAt ? fmtForInput(workshop.startsAt) : '',
    endsAt: workshop?.endsAt ? fmtForInput(workshop.endsAt) : '',
    locationType: workshop?.locationType || 'online',
    location: workshop?.location || '',
    meetingUrl: workshop?.meetingUrl || '',
    capacity: workshop?.capacity?.toString() || '',
    status: workshop?.status || 'upcoming',
    topicsText: (workshop?.topics || []).join('\n'),
    agendaText: (workshop?.agenda || []).map(a => `${a.time} | ${a.title}`).join('\n'),
  });
  const [presenters, setPresenters] = useState<Presenter[]>(initialPresenters);
  const update = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm(prev => ({ ...prev, [k]: v }));

  const updatePresenter = (idx: number, patch: Partial<Presenter>) => {
    setPresenters(prev => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
  };
  const addPresenter = () => setPresenters(prev => [...prev, { name: '', title: '', imageUrl: null }]);
  const removePresenter = (idx: number) => setPresenters(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const cleanPresenters = presenters
        .map(p => ({
          name: p.name.trim(),
          title: p.title?.trim() || null,
          imageUrl: p.imageUrl || null,
          bio: p.bio?.trim() || null,
        }))
        .filter(p => p.name);
      const payload = {
        title: form.title.trim(),
        slug: form.slug.trim() || undefined,
        subtitle: form.subtitle.trim() || null,
        description: form.description.trim() || null,
        coverImageUrl: form.coverImageUrl || null,
        presenters: cleanPresenters,
        startsAt: new Date(form.startsAt).toISOString(),
        endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
        locationType: form.locationType,
        location: form.location.trim() || null,
        meetingUrl: form.meetingUrl.trim() || null,
        capacity: form.capacity ? parseInt(form.capacity, 10) : null,
        status: form.status,
        topics: form.topicsText.split('\n').map(s => s.trim()).filter(Boolean),
        agenda: form.agendaText.split('\n').map(line => {
          const [time, ...rest] = line.split('|').map(s => s.trim());
          return time && rest.length ? { time, title: rest.join('|').trim() } : null;
        }).filter(Boolean),
      };
      if (isEdit) {
        const r = await apiClient.patch(`/admin/workshops/${workshop!.id}`, payload);
        return r.data.data;
      }
      const r = await apiClient.post(`/admin/workshops`, payload);
      return r.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-workshops'] });
      toast.success(isEdit ? 'Workshop updated' : 'Workshop created');
      onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Save failed'),
  });

  const canSave = form.title.trim() && form.startsAt && !saveMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit workshop' : 'New workshop'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Field label="Cover image" hint="Shown on the workshop page hero and in the public list">
            <ImageUpload
              value={form.coverImageUrl}
              onChange={(url) => update('coverImageUrl', url)}
              aspect="wide"
            />
          </Field>

          <Field label="Title *">
            <Input value={form.title} onChange={e => update('title', e.target.value)} placeholder="Intro to RoniDoc for Australian firms" />
          </Field>
          <Field label="Subtitle">
            <Input value={form.subtitle} onChange={e => update('subtitle', e.target.value)} placeholder="What you'll see in the next 60 minutes." />
          </Field>
          <Field label="Slug" hint="URL slug (auto-generated from title if blank)">
            <Input value={form.slug} onChange={e => update('slug', e.target.value)} placeholder="intro-to-ronidoc" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Starts at *" hint={form.startsAt ? `${new Date(form.startsAt).toLocaleString('en-AU', { dateStyle: 'full', timeStyle: 'short' })}` : 'Local time (your computer)'}>
              <Input
                type="datetime-local"
                value={form.startsAt}
                min={fmtForInput(new Date().toISOString())}
                onChange={e => update('startsAt', e.target.value)}
              />
            </Field>
            <Field label="Ends at" hint={form.endsAt ? `${new Date(form.endsAt).toLocaleString('en-AU', { dateStyle: 'full', timeStyle: 'short' })}` : '(optional)'}>
              <Input
                type="datetime-local"
                value={form.endsAt}
                min={form.startsAt || fmtForInput(new Date().toISOString())}
                onChange={e => update('endsAt', e.target.value)}
              />
            </Field>
          </div>

          <Field label="Location type">
            <div className="flex gap-2">
              {[{ k: 'online', l: 'Online' }, { k: 'in-person', l: 'In person' }, { k: 'hybrid', l: 'Hybrid' }].map(o => (
                <button
                  key={o.k}
                  type="button"
                  onClick={() => update('locationType', o.k)}
                  className={cn(
                    'flex-1 px-3 py-2 rounded-xl border text-sm transition',
                    form.locationType === o.k ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card hover:border-muted-foreground/40',
                  )}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </Field>

          {form.locationType !== 'online' && (
            <Field label="Address">
              <Input value={form.location} onChange={e => update('location', e.target.value)} placeholder="Level 5, 123 Collins St, Melbourne VIC 3000" />
            </Field>
          )}
          {form.locationType !== 'in-person' && (
            <Field label="Meeting URL" hint="Zoom / Teams / Meet link — sent to registrants by email">
              <Input value={form.meetingUrl} onChange={e => update('meetingUrl', e.target.value)} placeholder="https://us02web.zoom.us/j/..." />
            </Field>
          )}

          {/* Presenters — dynamic list */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-medium text-muted-foreground">Presenters</label>
              <Button type="button" size="sm" variant="outline" onClick={addPresenter}>
                <UserPlus className="h-3.5 w-3.5 mr-1.5" /> Add presenter
              </Button>
            </div>
            <div className="space-y-3">
              {presenters.map((p, idx) => (
                <div key={idx} className="flex gap-3 rounded-xl border border-border bg-muted/20 p-3">
                  <div className="w-24 flex-shrink-0">
                    <ImageUpload
                      value={p.imageUrl}
                      onChange={(url) => updatePresenter(idx, { imageUrl: url })}
                      aspect="square"
                    />
                  </div>
                  <div className="flex-1 space-y-2">
                    <Input
                      value={p.name}
                      onChange={e => updatePresenter(idx, { name: e.target.value })}
                      placeholder="Presenter name"
                    />
                    <Input
                      value={p.title || ''}
                      onChange={e => updatePresenter(idx, { title: e.target.value })}
                      placeholder="Title (e.g. Founder, RoniDoc)"
                    />
                    <Textarea
                      value={p.bio || ''}
                      onChange={e => updatePresenter(idx, { bio: e.target.value })}
                      placeholder="Short bio (optional)"
                      rows={2}
                      className="resize-none text-sm"
                    />
                  </div>
                  {presenters.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removePresenter(idx)}
                      className="self-start text-muted-foreground hover:text-rose-600 p-1.5 -mt-1 -mr-1"
                      aria-label="Remove presenter"
                    >
                      <Trash className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Capacity" hint="Blank for unlimited">
              <Input type="number" min="1" value={form.capacity} onChange={e => update('capacity', e.target.value)} placeholder="50" />
            </Field>
            <Field label="Status">
              <select
                value={form.status}
                onChange={e => update('status', e.target.value as any)}
                className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm"
              >
                <option value="upcoming">Upcoming</option>
                <option value="cancelled">Cancelled</option>
                <option value="completed">Completed</option>
              </select>
            </Field>
          </div>

          <Field label="Description" hint="Rich text — bold, lists, links all supported">
            <RichTextEditor
              value={form.description}
              onChange={(html) => update('description', html)}
              placeholder="Two-paragraph summary of what attendees will see and walk away with."
              minHeight={180}
            />
          </Field>

          <Field label="What you'll learn" hint="One bullet per line">
            <Textarea value={form.topicsText} onChange={e => update('topicsText', e.target.value)} placeholder={"How RoniDoc reads tax returns\nAutomating BAS preparation\nFinding tax opportunities you missed"} rows={4} />
          </Field>

          <Field label="Agenda" hint="One row per line, format: TIME | TITLE">
            <Textarea value={form.agendaText} onChange={e => update('agendaText', e.target.value)} placeholder={"10:00 | Welcome\n10:05 | Live walkthrough\n10:35 | Q&A"} rows={4} />
          </Field>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}><X className="h-4 w-4 mr-1.5" /> Cancel</Button>
          <Button disabled={!canSave} onClick={() => saveMutation.mutate()}>
            {saveMutation.isPending ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</> : isEdit ? 'Save changes' : 'Create workshop'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1.5">
        {label} {hint && <span className="text-[10px] text-muted-foreground/60 font-normal">· {hint}</span>}
      </label>
      {children}
    </div>
  );
}
